/**
 * parsePasteBlock — pure paste-block splitter behind the PasteImport mechanic
 * (§26). Kept in its own module (no component export) so it's unit-testable and
 * doesn't trip React Fast Refresh.
 */

/** One parsed record: the input cells (as typed), one per expected column. */
export interface ParsedRow {
  values: string[];
}

export interface ParseResult {
  rows: ParsedRow[];
  /** Lines that didn't yield the expected column count (skipped). */
  skipped: number;
}

/**
 * Split a pasted block into rows of exactly `cols` cells. Each non-empty line
 * is split on any run of whitespace, tabs, or commas. A line that doesn't
 * produce at least `cols` cells is skipped (counted). Extra trailing cells are
 * dropped so the row shape is stable.
 */
export function parsePasteBlock(block: string, cols: number): ParseResult {
  const rows: ParsedRow[] = [];
  let skipped = 0;
  for (const rawLine of block.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (!line) continue;
    const cells = line.split(/[\s,]+/).filter((c) => c.length > 0);
    if (cells.length < cols) {
      skipped += 1;
      continue;
    }
    rows.push({ values: cells.slice(0, cols) });
  }
  return { rows, skipped };
}
